import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/field';
import { Alert, Spinner } from '@/components/ui/misc';
import { AuthLayout } from './AuthLayout';

/** A one-time reset link issued on the server: choose a new password, then sign in with it. */
export function ResetPassword() {
  const { token = '' } = useParams();
  const url = `/api/password-reset/${encodeURIComponent(token)}`;
  const info = useQuery({
    queryKey: ['password-reset', token],
    queryFn: () => api.get<{ email: string }>(url),
    retry: false,
  });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (info.isLoading) return <Spinner className="h-full" />;
  if (done)
    return (
      <AuthLayout title="Password changed">
        <Alert tone="ok">Your password is set. Sign in with it and your authenticator code.</Alert>
        <Link to="/signin" className="mt-4 inline-block text-sm text-brand-text hover:underline">
          Go to sign in
        </Link>
      </AuthLayout>
    );
  if (info.error)
    return (
      <AuthLayout title="Reset link unavailable">
        <Alert tone="warn">{errorMessage(info.error)}</Alert>
        <Link to="/signin" className="mt-4 inline-block text-sm text-brand-text hover:underline">
          Go to sign in
        </Link>
      </AuthLayout>
    );

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) return setError('The passwords do not match.');
    setBusy(true);
    setError(null);
    try {
      await api.post(url, { password });
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle={
        <>
          For <span className="font-medium text-text">{info.data!.email}</span>. This link works once.
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        {error && <Alert tone="bad">{error}</Alert>}
        <Field label="New password" htmlFor="password" hint="At least 12 characters. A short phrase works well.">
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field label="Confirm password" htmlFor="confirm">
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
        <Button type="submit" size="lg" loading={busy}>
          Set password
        </Button>
      </form>
    </AuthLayout>
  );
}
